'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Label } from '~/components/ui/label';
import { cn } from '~/lib/utils';

const fieldVariants = cva('flex w-full flex-col gap-1.5', {
  variants: {
    orientation: {
      vertical: 'flex-col',
      horizontal: 'flex-row items-center gap-3',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-4', className)} data-slot="field-group" {...props} />;
}

function Field({
  className,
  orientation,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      className={cn(fieldVariants({ orientation }), className)}
      data-slot="field"
      {...props}
    />
  );
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return <Label className={cn('text-sm font-semibold', className)} data-slot="field-label" {...props} />;
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn('text-muted-foreground text-sm leading-5', className)}
      data-slot="field-description"
      {...props}
    />
  );
}

function FieldError({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn('text-destructive text-sm font-medium', className)}
      data-slot="field-error"
      role="alert"
      {...props}
    />
  );
}

export { Field, FieldGroup, FieldLabel, FieldDescription, FieldError };
